/* Hallmark · F6 Product card grid knobs: ratio=4/3 landscape, density=3-up, micro-action="View →"
 * S2 Hanging section heads. The page's primary job lives here: everything the code
 * can be spent on, each entry opening the booking search already scoped to it.
 */
import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { SiteThumb } from '@/components/search/search-bar/destination-items';
import { cn } from '@/lib/utils';
import {
  type ApplicableRate,
  type ApplicableService,
  type ApplicableSite,
  type VoucherApplicability,
  type VoucherScope,
  type VoucherScopeTarget,
} from '@/types/voucher';
import { Link } from '@inertiajs/react';
import { ArrowRight, MapPin, Ticket } from 'lucide-react';
import { type CSSProperties } from 'react';

const formatVND = (value: number) => new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(value);

/** The sentence that opens the section. Names the target rather than restating the scope. */
function leadCopy(scope: VoucherScope, target: VoucherScopeTarget | null, unresolved: boolean): string {
  if (unresolved) {
    return 'Chúng tôi chưa xác định được điểm áp dụng của mã này. Vui lòng liên hệ để được hỗ trợ.';
  }

  if (!target) {
    return 'Mã dùng được cho toàn bộ hệ thống. Chọn một điểm đến để bắt đầu.';
  }

  return {
    site: `Mã chỉ dùng được tại ${target.name}. Chọn gói phù hợp bên dưới.`,
    rate: `Mã chỉ dùng được cho gói ${target.name}, tại các điểm đến bên dưới.`,
    service: `Mã chỉ dùng được cho dịch vụ ${target.name}, thuộc các gói bên dưới.`,
    global: 'Mã dùng được cho toàn bộ hệ thống. Chọn một điểm đến để bắt đầu.',
  }[scope];
}

/** A hanging section head — label above content, single column, no left-margin rail. */
function BlockHead({ title, count }: { title: string; count: number }) {
  return (
    <div className="mb-4">
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      <p className="text-muted-foreground-strong mt-0.5 text-sm">{count} mục</p>
    </div>
  );
}

function SiteCard({ site }: { site: ApplicableSite }) {
  const href = site.mapping_code ? SiteInertiaController.search({ query: { site: site.mapping_code } }).url : null;

  const body = (
    <>
      <SiteThumb site={site} className="h-32 w-full rounded-lg object-cover" fallbackClassName="h-32 w-full rounded-lg" />
      <div className="mt-3 min-w-0">
        <p className="truncate font-semibold text-gray-900">{site.name}</p>
        {site.city && (
          <p className="text-muted-foreground-strong mt-1 flex items-center gap-1 text-sm">
            <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{site.city}</span>
          </p>
        )}
      </div>
    </>
  );

  const shell = 'rounded-xl border border-(--pill-border) bg-white p-5';

  // A site with no mapping_code cannot be opened in search — render it as a plain
  // card rather than a link that goes nowhere.
  if (!href) {
    return <div className={shell}>{body}</div>;
  }

  return (
    <Link
      href={href}
      className={cn(
        shell,
        'group block outline-none',
        'hover:shadow-card focus-visible:ring-primary/60 focus-visible:ring-[3px]',
        'motion-safe:hover:-translate-y-0.5 motion-safe:active:translate-y-0 motion-safe:transition-[box-shadow,transform] motion-safe:duration-200',
      )}
    >
      {body}
      <span className="text-primary-ink mt-3 inline-flex items-center gap-1 text-sm font-semibold whitespace-nowrap">
        Đặt vé
        <ArrowRight className="size-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

function RateCard({ rate }: { rate: ApplicableRate }) {
  const href = rate.site_mapping_code ? SiteInertiaController.search({ query: { site: rate.site_mapping_code } }).url : null;

  const body = (
    <div className="min-w-0">
      <p className="font-semibold text-gray-900">{rate.name}</p>
      {rate.type_name && (
        <span className="mt-2 inline-block rounded-full border border-(--pill-border) bg-(--pill-bg) px-2.5 py-1 text-xs text-gray-600">
          {rate.type_name}
        </span>
      )}
    </div>
  );

  const shell = 'rounded-xl border border-(--pill-border) bg-white p-5';

  if (!href) {
    return <div className={shell}>{body}</div>;
  }

  return (
    <Link
      href={href}
      className={cn(
        shell,
        'group flex items-center justify-between gap-4 outline-none',
        'hover:shadow-card focus-visible:ring-primary/60 focus-visible:ring-[3px]',
        'motion-safe:hover:-translate-y-0.5 motion-safe:active:translate-y-0 motion-safe:transition-[box-shadow,transform] motion-safe:duration-200',
      )}
    >
      {body}
      {/* No price: the amount a visitor pays comes from the live rate API per date,
          so it is shown on the search page rather than guessed at here. */}
      <span className="text-primary-ink inline-flex shrink-0 items-center gap-1 text-sm font-semibold whitespace-nowrap">
        Xem giá
        <ArrowRight className="size-4 motion-safe:transition-transform motion-safe:group-hover:translate-x-0.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

/** Services are not addressable in search, so they read as a spec list, not as links. */
function ServiceRow({ service }: { service: ApplicableService }) {
  return (
    <li className="flex items-center justify-between gap-4 py-3">
      <div className="flex min-w-0 items-center gap-2">
        <Ticket className="text-primary size-4 shrink-0" aria-hidden="true" />
        <span className="truncate text-gray-900">{service.name}</span>
      </div>
      <div className="text-muted-foreground-strong shrink-0 text-sm">
        {service.original_price && service.original_price > 0 ? formatVND(service.original_price) : service.type_name}
      </div>
    </li>
  );
}

interface ApplicableServicesProps {
  applicability: VoucherApplicability;
}

export function ApplicableServices({ applicability }: ApplicableServicesProps) {
  const { scope, target, unresolved, sites, rates, services } = applicability;
  const isEmpty = sites.length === 0 && rates.length === 0 && services.length === 0;

  return (
    <div>
      <p className="mb-8 max-w-2xl text-base text-gray-700">{leadCopy(scope, target, unresolved)}</p>

      {isEmpty && (
        <p className="rounded-xl border border-(--pill-border) bg-(--pill-bg) p-5 text-sm text-gray-700">
          Hiện chưa có điểm đến nào đang mở bán cho ưu đãi này.
        </p>
      )}

      {sites.length > 0 && (
        <section className="mb-10">
          <BlockHead title="Điểm đến" count={sites.length} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {sites.map((site) => (
              <SiteCard key={site.id} site={site} />
            ))}
          </div>
        </section>
      )}

      {rates.length > 0 && (
        <section className="mb-10">
          <BlockHead title="Gói dịch vụ" count={rates.length} />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {rates.map((rate) => (
              <RateCard key={rate.id} rate={rate} />
            ))}
          </div>
        </section>
      )}

      {services.length > 0 && (
        <section>
          <BlockHead title="Dịch vụ" count={services.length} />
          <ul className="divide-y rounded-xl border border-(--pill-border) bg-white px-5" style={{ '--tw-divide-color': 'var(--pill-border)' } as CSSProperties}>
            {services.map((service) => (
              <ServiceRow key={service.id} service={service} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
